import { Alert, Box, Header, SpaceBetween, Spinner } from '@amzn/awsui-components-react-v3';
import Table from '@amzn/awsui-components-react-v3/polaris/table';
import React, { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import TablePagination from '../../../../components/Table/TablePagination';
import TablePreferences from '../../../../components/Table/TablePreferences';
import { SortOrder } from '../../../../types';
import { columnDefinitions, PAGE_SIZE_PREFERENCES, VISIBLE_COLUMNS_PREFERENCES } from './config';
import TableFilter from './TableFilter';
import useSearchExceptions from './useSearchExceptions';
import './style.css';
import { useRouteLoadDurationPublisher } from '@awsscm/argo-core/argo/utils/metrics/ArgoRouteLoadDurationContext';

export default function SearchException(): ReactElement {
  const publishRouteLoadDuration = useRouteLoadDurationPublisher();
  const { t } = useTranslation();
  const {
    filters,
    items,
    loading,
    error,
    pageCount,
    pageIndex,
    totalCount,
    sortBy,
    sortOrder,
    pageSize,
    setPageSize,
    visibleColumns,
    setVisibleColumns,
    setError,
    onPaginationChange,
    onSortingChange,
    onSearch,
    onPreferenceChange,
    fetchConfigError,
    exceptionTypes,
    loadingResolverConfig,
  } = useSearchExceptions();
  publishRouteLoadDuration();

  if (fetchConfigError) {
    return <Alert type="error">{fetchConfigError}</Alert>;
  }
  // Do not load page until exception type and resovler group info is done being loaded.
  if (loading || loadingResolverConfig) {
    return <Spinner size="big" />;
  }
  return (
    <div className="container-max-width">
      <SpaceBetween direction="vertical" size="s">
        <Header
          data-test-id="page-header"
          variant="h1"
          description={t('Search for open exceptions, update, and resolve them')}>
          {t('Search Exceptions')}
        </Header>
        {error && (
          <Box margin={{ top: 'm', bottom: 'm' }}>
            <Alert type="error" onDismiss={() => setError('')}>
              {error}
            </Alert>
          </Box>
        )}
        <Table
          visibleColumns={visibleColumns}
          trackBy="id"
          header={<Header counter={`(${totalCount})`}>{t('Exceptions')}</Header>}
          filter={
            <TableFilter
              onSearch={onSearch}
              filters={filters}
              loading={loading}
              exceptionTypes={exceptionTypes}
            />
          }
          columnDefinitions={columnDefinitions}
          items={items}
          loading={loading}
          loadingText={t('Loading exceptions')}
          empty={
            <Box textAlign="center" color="inherit">
              <b>{t('No exceptions')}</b>
              <Box padding={{ bottom: 's' }} variant="p" color="inherit">
                {t('No exceptions to display')}
              </Box>
            </Box>
          }
          sortingColumn={{ sortingField: sortBy }}
          sortingDescending={sortOrder === SortOrder.DESC}
          onSortingChange={(e) => onSortingChange(e.detail)}
          sortingDisabled={loading}
          pagination={
            <TablePagination
              currentPageIndex={pageIndex}
              pagesCount={pageCount}
              onPaginationChange={onPaginationChange}
            />
          }
          preferences={
            <TablePreferences
              pageSize={pageSize}
              setPageSize={setPageSize}
              visibleColumns={visibleColumns}
              setVisibleColumns={setVisibleColumns}
              pageSizePreferences={PAGE_SIZE_PREFERENCES}
              visibleContentPreferences={VISIBLE_COLUMNS_PREFERENCES}
              onPreferenceChange={onPreferenceChange}
            />
          }
        />
      </SpaceBetween>
    </div>
  );
}
